import { NgClass } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { PlanCategory, SubscriptionStatus } from '@fitness-force/enum';
import { BehaviorSubject, Observable } from 'rxjs';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';

const STATUS_BADGE_BASE =
  'inline-flex align-items-center gap-1 white-space-nowrap text-xs px-2 py-1 border-round font-medium border-1 uppercase';

@Component({
  selector: 'subscription-name',
  templateUrl: './subscription-name.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, DateTimeFormatPipe, TranslateModule, TooltipModule],
})
export class SubscriptionNameComponent implements OnInit {
  private readonly cd = inject(ChangeDetectorRef);

  additionData: Observable<any> = new BehaviorSubject(null);
  columnData: Observable<any> = new BehaviorSubject(null);

  subscriptionName = '';
  subscriptionStatus = '';
  subscriptionCategory = '';
  subscriptionDisplayTitle = '';
  statusClass = '';
  statusDotClass = '';
  categoryClass = '';
  showCategory = false;
  showSubscriptionDates = false;
  showInfiniteEndDate = false;
  subscriptionStartDate: string | null = null;
  subscriptionEndDate: string | null = null;
  showManualOverrideBadge = false;

  ngOnInit() {
    this.columnData.subscribe({
      next: (item: any) => {
        this.setSubscriptionDetails(item);
      },
    });
    this.additionData.subscribe({
      next: (adata: { showSubscriptionDates?: boolean } | null) => {
        this.showSubscriptionDates = adata?.showSubscriptionDates === true;
        this.cd.detectChanges();
      },
    });
  }

  setSubscriptionDetails(data: any) {
    const subscription =
      data?.client_subscription ?? data?.subscription ?? data?.active_subscription;
    if (!subscription) {
      return;
    }

    this.subscriptionName = subscription.name_primary_language || subscription.subscription_name || 'N/A';
    this.subscriptionStatus = this.getStatusLabel(subscription.status);
    this.statusClass = this.getStatusClass(subscription.status);
    this.statusDotClass = this.getStatusDotClass(subscription.status);
    const planCategory = this.getPlanCategory(subscription) ?? '';
    this.subscriptionCategory = this.getCategoryDisplay(planCategory);
    this.showCategory = this.subscriptionCategory !== 'N/A';
    this.categoryClass = this.getCategoryClass(planCategory);
    this.subscriptionDisplayTitle = this.showCategory
      ? `${this.subscriptionName} - ${this.subscriptionCategory}`
      : this.subscriptionName;
    this.subscriptionStartDate = this.getValidDate(subscription.start_date);
    const normalizedCategory = this.normalizeCategoryKey(planCategory);
    this.showInfiniteEndDate =
      this.isPayAsYouGoCategory(normalizedCategory) ||
      this.isMaxEndDate(subscription.end_date);
    this.subscriptionEndDate = this.showInfiniteEndDate
      ? null
      : this.getValidDate(subscription.end_date);
    this.showManualOverrideBadge = subscription.is_status_overridden === true;

    this.cd.detectChanges();
  }

  private getPlanCategory(subscription: {
    subscription_category?: string | number;
    category?: string | number;
  }): string | number | undefined {
    return subscription.subscription_category ?? subscription.category;
  }

  private isPayAsYouGoCategory(normalizedCategory: string): boolean {
    return (
      normalizedCategory === 'payasyougo' ||
      normalizedCategory === 'converttopayasyougo'
    );
  }

  private isMaxEndDate(date?: string | Date | null): boolean {
    if (!date) {
      return false;
    }

    const parsedDate =
      date instanceof Date
        ? date
        : new Date(typeof date === 'string' ? date : String(date));

    if (Number.isNaN(parsedDate.getTime())) {
      return false;
    }

    return parsedDate.getFullYear() >= 9998;
  }

  private getValidDate(date?: string | Date | null): string | null {
    if (!date) {
      return null;
    }

    const dateStr = typeof date === 'string' ? date : date.toString();
    if (dateStr.startsWith('0001-01-01')) {
      return null;
    }

    return dateStr;
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return 'Unknown';
    }
    return status
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
  }

  private normalizeStatus(status?: string): string {
    if (!status) {
      return '';
    }

    const trimmed = status.trim();
    if (/^\d+$/.test(trimmed)) {
      const enumKey = SubscriptionStatus[Number(trimmed) as SubscriptionStatus];
      if (typeof enumKey === 'string') {
        return enumKey.toLowerCase();
      }
    }

    return trimmed.toLowerCase().replace(/[\s_-]/g, '');
  }

  private getStatusClass(status?: string): string {
    switch (this.normalizeStatus(status)) {
      case 'active':
        return `${STATUS_BADGE_BASE} bg-green-100 text-green-800 border-green-200`;
      case 'frozen':
      case 'future':
        return `${STATUS_BADGE_BASE} bg-blue-100 text-blue-800 border-blue-200`;
      case 'pending':
      case 'pendingformandatoryaction':
      case 'pendingforpaymentconfirmation':
        return `${STATUS_BADGE_BASE} bg-yellow-100 text-yellow-800 border-yellow-200`;
      case 'inactive':
      case 'suspended':
      case 'suspendedduetononpayment':
      case 'cancelled':
      case 'cancelledduetononpayment':
      case 'void':
      case 'notrenewing':
        return `${STATUS_BADGE_BASE} bg-red-100 text-red-800 border-red-200`;
      case 'expired':
        return `${STATUS_BADGE_BASE} bg-gray-100 text-gray-800 border-gray-200`;
      case 'upgraded':
      case 'transfered':
        return `${STATUS_BADGE_BASE} bg-indigo-100 text-indigo-800 border-indigo-200`;
      default:
        return `${STATUS_BADGE_BASE} bg-gray-100 text-gray-600 border-gray-200`;
    }
  }

  private getStatusDotClass(status?: string): string {
    switch (this.normalizeStatus(status)) {
      case 'active':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-green-500';
      case 'frozen':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-blue-500';
      case 'pending':
      case 'pendingformandatoryaction':
      case 'pendingforpaymentconfirmation':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-yellow-500';
      case 'inactive':
      case 'suspended':
      case 'suspendedduetononpayment':
      case 'cancelled':
      case 'cancelledduetononpayment':
      case 'void':
      case 'notrenewing':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-red-500';
      case 'expired':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-gray-500';
      case 'upgraded':
      case 'transfered':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-indigo-500';
      case 'future':
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-blue-500';
      default:
        return 'border-circle w-0.5rem h-0.5rem flex-shrink-0 bg-gray-400';
    }
  }

  private getCategoryClass(category: string | number): string {
    const normalized = this.normalizeCategoryKey(category);

    switch (normalized) {
      case 'contract':
        return 'border-1 bg-purple-100 text-purple-800 border-purple-200';
      case 'payasyougo':
      case 'converttopayasyougo':
        return 'border-1 bg-orange-100 text-orange-800 border-orange-200';
      case 'membership':
        return 'border-1 bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'paidinfull':
        return 'border-1 bg-teal-100 text-teal-800 border-teal-200';
      case 'dropin':
        return 'border-1 bg-pink-100 text-pink-800 border-pink-200';
      default:
        return 'border-1 bg-gray-100 text-gray-600 border-gray-200';
    }
  }

  private getCategoryDisplay(category: string | number): string {
    if (category === null || category === undefined || category === '') {
      return 'N/A';
    }

    const normalized = this.normalizeCategoryKey(category);

    switch (normalized) {
      case 'payasyougo':
      case 'converttopayasyougo':
        return 'Pay As You Go';
      case 'contract':
        return 'Contract';
      case 'paidinfull':
        return 'Paid In Full';
      case 'dropin':
        return 'Drop In';
      case 'membership':
        return 'Membership';
      default:
        return typeof category === 'string' ? category : normalized;
    }
  }

  private normalizeCategoryKey(category: string | number): string {
    if (category === null || category === undefined || category === '') {
      return '';
    }

    if (typeof category === 'number') {
      const enumKey = PlanCategory[category as PlanCategory];
      return typeof enumKey === 'string' ? enumKey.toLowerCase() : String(category);
    }

    const trimmed = category.trim();
    if (/^\d+$/.test(trimmed)) {
      const enumKey = PlanCategory[Number(trimmed) as PlanCategory];
      if (typeof enumKey === 'string') {
        return enumKey.toLowerCase();
      }
    }

    return trimmed.replace(/[\s_-]/g, '').toLowerCase();
  }
}
